/// <amd-module name="WorkItemsHub/Actions/ActionsCreator" />
import { ActionsHub } from "WorkItemsHub/Actions/ActionsHub";
import { WorkItemsHubDataProvider } from "WorkItemsHub/DataProviders/WorkItemsHubDataProvider";
import { IVssPageContext } from "VSS/Platform/Context";
import { WorkItemsHubData, WorkItemsHubSortOption, WorkItemsHubPagedData } from "Generated/Contracts";
import { FilterState } from "Wit/Filter/FilterManager";
import { IWorkItemsHubFilterDataSource } from "WorkItemsHub/Stores/IWorkItemsHubFilterDataSource";
import { traceError } from "VSS/Platform/Trace";
import { TelemetryConstants } from "WorkItemsHub/Utils/Telemetry";
import { WorkItemsHubPaging } from "WorkItemsHub/Generated/Constants";

export class ActionsCreator {
    constructor(private _pageContext: IVssPageContext, private _actionsHub: ActionsHub) {}

    /**
     * Invalidates the cached data provider for a specifc tab.
     * @param tabId id of tab
     */
    public invalidateTabData(tabId: string): void {
        this._actionsHub.invalidateTabData.invoke(tabId);
    }

    /**
     * Invalidates the cached data provider asynchronously.
     * @param tabId id of tab to refresh data for
     * @param invalidateAllTabsData If true, invalidates all tabs' data. Otherwise only the current tab data will be invalidated
     */
    public refreshDataProviderAsync(tabId: string, invalidateAllTabsData: boolean = false, pageSize: number = WorkItemsHubPaging.MaxWorkItems): void {
        if (invalidateAllTabsData) {
            this._actionsHub.invalidateAllTabsData.invoke(null);
        }

        WorkItemsHubDataProvider.refreshDataAsync(this._pageContext, tabId, pageSize).then(
            (tabData: WorkItemsHubData) => {
                this._actionsHub.endRefreshDataProvider.invoke({ tabId, tabData });
            },
            (reason) => {
                this._actionsHub.endRefreshDataProvider.invoke({ tabId, tabData: null });
                traceError(this._pageContext, TelemetryConstants.Area, "refreshDataAsync", reason);
            }
        );
    }

    /**
     * Removes the specified items from the current tab and invalidates the cached data provider for other tabs.
     * @param tabId id of tab to refresh data for
     * @param workItemIds ids of the work items to be removed from the view
     */
    public removeWorkItemFromDataProvider(tabId: string, workItemIds: number[]): void {
        this._actionsHub.removeWorkItems.invoke({ tabId, workItemIds });
    }

    /**
     * Updates the specific tab with filter data.
     * @param tabId the tabId to update filter
     * @param filterState new filter state
     */
    public updateFilterState(tabId: string, filterState: FilterState): void {
        this._actionsHub.updateFilter.invoke({
            tabId: tabId,
            updatedFilter: filterState
        });
    }

    /**
     * Sorts the specific tab on a particular column.
     * @param tabId tabId
     * @param sortOptions array of columns to be sorted on
     */
    public sortColumn(tabId: string, sortOptions: WorkItemsHubSortOption[]): void {
        this._actionsHub.sortItems.invoke({ tabId, sortOptions });
    }

    /**
     * Sets the visibility of completed work items.
     * @param tabId tabId
     * @param showCompletedItems if true, completed work items will be shown. otherwise they will be hidden
     */
    public setCompletedItemsVisibility(tabId: string, showCompletedItems: boolean): void {
        this._actionsHub.setCompletedItemsVisibility.invoke({ tabId, showCompletedItems });
    }

    /**
     * Pages the specified items for the given tab
     * @param tabId id of tab to page data for
     * @param dataSource data source to page data for
     */
    public pageWorkItems(tabId: string, dataSource: IWorkItemsHubFilterDataSource): void {
        const workItemIds = dataSource.getNextPageIds();
        const fieldReferenceNames = dataSource.getPageableFieldReferenceNames();

        WorkItemsHubDataProvider.pageWorkItems(this._pageContext, tabId, workItemIds, fieldReferenceNames)
            .then((payload: WorkItemsHubPagedData) => {
                this._actionsHub.endPageWorkItems.invoke({ tabId, fieldReferenceNames, fieldValuesList: payload.fieldValues });
            })
            .then(null, error => {
                traceError(this._pageContext, TelemetryConstants.Area, "pageWorkItems", error);
            });
    }
}
